import React from "react";
import { Link, useLocation } from "react-router-dom";
import type { Post } from "../types";
import { formatBlackspaceDateTime } from "../utils/time";
import "./PostItem.css";

type PostItemProps = {
  post: Post;
  currentUsername?: string;
  onSelfClick?: () => void;
  onAuthorNavigate?: (postId: string) => void;
};

function cleanUsername(value?: string | null) {
  return String(value || "").trim().replace(/^@+/, "");
}

export default function PostItem({ post, currentUsername, onSelfClick, onAuthorNavigate }: PostItemProps) {
  const location = useLocation();
  const authorUsername = cleanUsername(post.author?.username);
  const authorName = post.author?.displayName || authorUsername || "Anonymous";
  const spaceName = post.space?.name || "";
  const spaceId = String(post.space?.id || (spaceName === "Blackspace" ? "blackspace" : "")).trim();
  const spacePath = spaceId ? "/spaces/" + encodeURIComponent(spaceId) : "";
  const spaceAccent = /^#[0-9a-fA-F]{6}$/.test(post.space?.accentColor || "")
    ? post.space.accentColor
    : "#22d3ee";
  const spaceStyle = { "--bs-post-space-accent": spaceAccent } as React.CSSProperties;
  const avatarInitial = authorName.slice(0, 1).toUpperCase();
  const isBlackbox = authorUsername?.toLowerCase() === "blackbox";
  const isAnonymous = post.source === "anonymous" || authorUsername?.toLowerCase() === "anonymous";
  const isSelf = !!(
    currentUsername &&
    authorUsername &&
    authorUsername.toLowerCase() === cleanUsername(currentUsername).toLowerCase()
  );
  const createdAt = formatBlackspaceDateTime(post.createdAt);

  const rememberAuthorNavigation = () => {
    if (post.id) onAuthorNavigate?.(String(post.id));
  };

  const authorLink = (children: React.ReactNode, className?: string, openProfile = false) => {
    if (!authorUsername) return <span className={className}>{children}</span>;
    if (isBlackbox || isAnonymous) return <span className={className}>{children}</span>;
    if (isSelf && onSelfClick && !openProfile) {
      return (
        <span
          onClick={(event) => {
            event.stopPropagation();
            onSelfClick();
          }}
          className={`cursor-pointer ${className || ""}`}
        >
          {children}
        </span>
      );
    }

    return (
      <Link
        to={`/user/${encodeURIComponent(authorUsername)}`}
        state={location.pathname === `/user/${encodeURIComponent(authorUsername)}` ? location.state : {
          from: `${location.pathname}${location.search}`,
          label: location.pathname === "/home" ? "Feed" : "Previous page",
          postId: post.id,
          restoreFeed: location.pathname === "/home",
        }}
        className={className}
        onMouseDown={rememberAuthorNavigation}
        onTouchStart={rememberAuthorNavigation}
        onClick={(event) => {
          event.stopPropagation();
          rememberAuthorNavigation();
        }}
      >
        {children}
      </Link>
    );
  };

  return (
    <article>
      <header className="flex items-start justify-between gap-3 pr-9 sm:gap-4 sm:pr-10">
        <div className="flex min-w-0 items-start gap-2.5 sm:gap-3">
          {post.author?.avatarUrl ? (
            authorLink(
              <img
                src={post.author.avatarUrl}
                alt=""
                className="h-10 w-10 shrink-0 rounded-xl border border-white/10 object-cover shadow-[0_0_18px_rgba(168,85,247,0.08)]"
              />,
              "shrink-0"
            )
          ) : (
            authorLink(
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border text-base font-black shadow-[0_0_18px_rgba(168,85,247,0.08)] ${
                  isBlackbox
                    ? "border-cyan-300/30 bg-cyan-300/10 text-cyan-100 shadow-lg shadow-cyan-500/10"
                    : isAnonymous
                      ? "border-violet-300/30 bg-violet-300/10 text-violet-100 shadow-lg shadow-violet-500/10"
                      : "bs-elevated text-cyan-300"
                }`}
              >
                {avatarInitial}
              </div>,
              isBlackbox || isAnonymous ? "shrink-0" : "shrink-0 transition-transform hover:scale-[1.03]"
            )
          )}

          <div className="bs-post-author-details min-w-0 pt-0.5 sm:pt-1">
            <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <h3 className="bs-post-author-name max-w-full truncate text-sm font-black leading-tight text-neutral-50 sm:text-base">
                {authorLink(
                  authorName,
                  isBlackbox
                    ? "text-cyan-100"
                    : isAnonymous
                      ? "text-violet-100"
                      : "hover:text-cyan-300 transition-colors"
                )}
              </h3>
              {spaceName && (spacePath ? (
                <Link
                  to={spacePath}
                  title={spaceName}
                  state={{ from: location.pathname + location.search, label: location.pathname === "/home" ? "Feed" : "Previous page", postId: post.id, restoreFeed: location.pathname === "/home" }}
                  className="bs-post-space"
                  style={spaceStyle}
                  onMouseDown={rememberAuthorNavigation}
                  onTouchStart={rememberAuthorNavigation}
                  onClick={(event) => { event.stopPropagation(); rememberAuthorNavigation(); }}
                >{spaceName}</Link>
              ) : <span className="bs-post-space" style={spaceStyle}>{spaceName}</span>)}
              {post.lockedByPresence && !isBlackbox && (
                <span className="rounded-full border border-cyan-300/20 bg-cyan-300/10 px-2 py-0.5 text-[11px] font-black text-cyan-100">
                  Sealed
                </span>
              )}
            </div>

            <div className="bs-post-meta bs-muted mt-1 flex flex-wrap items-center gap-1.5 text-[11px] sm:text-xs">
              {authorUsername && authorLink(
                "@" + authorUsername,
                "bs-post-username hover:underline underline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-cyan-300",
                true
              )}
              {createdAt && <span className="text-violet-300/70">·</span>}
              {createdAt && <span>{createdAt}</span>}
            </div>
          </div>
        </div>
      </header>

      <p className="bs-post-body mt-2 whitespace-pre-wrap text-sm leading-6 text-neutral-100">{post.content}</p>
    </article>
  );
}
